import { NetworkInterceptor, NetworkRequestConfig, NetworkResponse, NetworkError } from '../interfaces/network.types';
import { sanitizeObject } from '../../utils/sanitizer';

/**
 * SanitizationInterceptor
 * 
 * A critical defense-in-depth security layer that automatically and 
 * recursively sanitizes all outgoing request payloads.
 * 
 * DESIGN RATIONALE:
 * - Prevents XSS and injection attacks by escaping HTML-sensitive characters 
 *   at the network boundary before they reach the backend.
 * - Centralizes security logic to ensure consistent enforcement across 
 *   all API services, eliminating reliance on manual per-component sanitization.
 * - Follows the "Zero-Trust" mandate: Always sanitize data before transmission.
 * 
 * @security Only sanitizes 'data' (payloads). URL parameters are typically 
 * encoded by the browser/NetworkService, but payloads require explicit 
 * structural sanitization.
 */
export class SanitizationInterceptor implements NetworkInterceptor {
    /**
     * Intercepts outgoing requests to recursively sanitize the data payload.
     * 
     * @param config - The incoming network configuration.
     * @returns Augmented configuration with sanitized data.
     */
    onRequest(config: NetworkRequestConfig): NetworkRequestConfig {
        // Handle Query Parameters (URL Search Params)
        if (config.params && typeof config.params === 'object') {
            config.params = sanitizeObject(config.params);
        }

        // Handle standard JSON payloads
        if (config.data && typeof config.data === 'object' && !(config.data instanceof FormData)) {
            return {
                ...config,
                data: sanitizeObject(config.data),
            };
        }

        // Handle FormData (e.g., file uploads with descriptions)
        //! SECURITY: Sanitize text fields within FormData while preserving Blobs/Files
        if (config.data instanceof FormData) {
            const sanitizedFormData = new FormData();
            config.data.forEach((value, key) => {
                if (typeof value === 'string') {
                    // JSON Detection: 
                    // Robust check for stringified JSON payloads commonly used in multipart/form-data.
                    const trimmed = value.trim();
                    const isPotentiallyJson = (trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'));

                    if (isPotentiallyJson && trimmed.length > 2) {
                        try {
                            const parsed = JSON.parse(trimmed);
                            
                            // SECURITY: Only process if it parsed into a non-null object/array
                            if (parsed && typeof parsed === 'object') {
                                // Recursively sanitize the parsed structure (Project Rule 07)
                                const sanitized = sanitizeObject(parsed);
                                sanitizedFormData.append(key, JSON.stringify(sanitized));
                                return;
                            }
                        } catch (e) {
                            // Non-critical: If it looked like JSON but failed to parse, 
                            // we treat it as a standard string and apply default sanitization.
                        }
                    }
                    
                    // Fallback to standard string sanitization for non-JSON or malformed data
                    sanitizedFormData.append(key, sanitizeObject(value));
                } else {
                    // Blobs/Files are preserved as-is (binary integrity mandate)
                    sanitizedFormData.append(key, value);
                }
            });
            return {
                ...config,
                data: sanitizedFormData,
            };
        }

        return config;
    }

    /**
     * Post-process responses to ensure no malicious payloads from the backend 
     * are rendered in the UI. 
     * 
     * @security Zero-Trust: "Never trust external data, even from your own backend."
     * Recursively sanitizes all string fields in the response data.
     */
    onResponse(response: NetworkResponse): NetworkResponse {
        if (response.data && typeof response.data === 'object' && !(response.data instanceof Blob)) {
            return {
                ...response,
                data: sanitizeObject(response.data),
            };
        }

        return response;
    }

    /**
     * Handle errors gracefully.
     */
    onError(error: NetworkError): NetworkError {
        return error;
    }
}
